/**
 * "Thinking" status shown while an assistant reply is being generated, before the
 * first token streams in. When the engine reports a REAL step via brew.progress
 * ("Searching the web", "Creating your image"), we show that. Otherwise we show a
 * NEUTRAL typing indicator (just animated dots) — never a fabricated "thinking"
 * line. A canned "Warming up / Brewwing your answer" rotation performed activity
 * the model wasn't doing; that's removed.
 *
 * A model's GENUINE chain-of-thought, when it produces one, renders separately in
 * the collapsible Thinking panel (see ThinkingTrail). This is only the pre-token wait.
 */

/**
 * Placeholder shown while a picture is being generated.
 *
 * A line of text is the wrong shape for this wait: image generation takes 5–20s
 * and the reply arrives in ONE chunk, so there is no token trickle to imply
 * progress. A frame the size of the thing being made says "a picture is coming,
 * and it goes here" at a glance, so the wait reads as accounted for rather than
 * hung. Deliberately NOT a percentage bar — the provider gives us no progress
 * signal, and a bar that isn't measuring anything is a lie.
 */
export function ImageGenStatus({ label }: { label?: string | undefined }): JSX.Element {
  return (
    <span className="imagegen" aria-live="polite">
      <span className="imagegen__frame" aria-hidden="true">
        <span className="imagegen__shimmer" />
        <svg className="imagegen__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="8.5" cy="9.5" r="1.8" stroke="currentColor" strokeWidth="1.6" />
          <path d="M4 17l4.5-4.5 3 3L15.5 11l4.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="imagegen__label">
        {label || 'Creating your image'}
        <span className="msg__thinking-dots" aria-hidden="true">
          <span>.</span><span>.</span><span>.</span>
        </span>
      </span>
    </span>
  );
}

interface Props {
  /** What the engine says it's actually doing, from its `brew.progress` events.
   *  When present it REPLACES the rotation: a real step ("Creating your image…")
   *  beats a guess, and the rotation exists only because we usually have none.
   *  It also stops the "Thinking" line contradicting a turn that is plainly
   *  drawing a picture. */
  label?: string | undefined;
}

export function ThinkingStatus({ label }: Props = {}): JSX.Element {
  // Honest wait: show the engine's REAL step when it reports one; otherwise just a
  // neutral typing indicator (the animated dots). No fabricated "thinking" text —
  // that's precisely what we're removing.
  return (
    <span className="msg__thinking" aria-live="polite">
      {label ? <span className="msg__thinking-text">{label}</span> : null}
      <span className="msg__thinking-dots" aria-hidden="true">
        <span>.</span><span>.</span><span>.</span>
      </span>
    </span>
  );
}
